import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Typography,
    Alert,
    Tooltip,
    CircularProgress,
    Checkbox,
    TextField
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Info as InfoIcon,
    Article as ArticleIcon,
} from '@mui/icons-material';

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// Database configuration
const DB_NAME = 'FinancialToolDB';
const DB_VERSION = 1;
const STORE_NAME = 'files';

// Storage keys for regular localStorage items
const STORAGE_KEYS = {
    FILE_PARAMS: 'financial_tool_file_params',
    BIZ_TYPE: 'financial_tool_biz_type',
    WEIGHTED_AVERAGE: 'financial_tool_weighted_average',
    WEIGHTS: 'financial_tool_weights',
};

// Helper function to open the IndexedDB database
const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            reject(`IndexedDB error: ${event.target.error}`);
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
    });
};

// Helper functions for IndexedDB operations
const dbHelpers = {
    saveFiles: async (files) => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            // Clear existing files
            store.clear();

            // Store each file with a unique ID
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                await store.add({ id: i, file });
            }

            return true;
        } catch (error) {
            console.error('Error saving files to IndexedDB:', error);
            return false;
        }
    },

    getFiles: async () => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const request = store.getAll();

                request.onsuccess = () => {
                    // Sort by ID and extract just the file objects
                    const sortedFiles = request.result
                        .sort((a, b) => a.id - b.id)
                        .map(item => item.file);
                    resolve(sortedFiles);
                };

                request.onerror = (event) => {
                    reject(`Error retrieving files: ${event.target.error}`);
                };
            });
        } catch (error) {
            console.error('Error accessing IndexedDB:', error);
            return [];
        }
    },

    clearFiles: async () => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            store.clear();
            return true;
        } catch (error) {
            console.error('Error clearing files from IndexedDB:', error);
            return false;
        }
    }
};

const FileUpload = ({ onSubmit, loading, error }) => {
    const [files, setFiles] = useState([]);
    const [fileParams, setFileParams] = useState([]);
    const [bizType, setBizType] = useState('auto');
    const [dragActive, setDragActive] = useState(false);
    const [loadingState, setLoadingState] = useState(true);
    const [useWeightedAverage, setUseWeightedAverage] = useState(false);
    const [weights, setWeights] = useState({});
    
    const fileInputRef = useRef(null);

    // Helper function to identify full year files
    const getFullYearFiles = () => {
        return fileParams
            .map((params, index) => ({ ...params, index, file: files[index] }))
            .filter(item => item.months === '12');
    };

    // Helper function to identify partial year files
    const getPartialYearFiles = () => {
        return fileParams
            .map((params, index) => ({ ...params, index, file: files[index] }))
            .filter(item => item.months !== '12');
    };

    // Check if weighted average should be available
    const shouldShowWeightedAverage = () => {
        const fullYearFiles = getFullYearFiles();
        const partialYearFiles = getPartialYearFiles();
        
        // Only show if we have at least 2 full year files and at least 1 partial year file
        return fullYearFiles.length >= 1 && partialYearFiles.length >= 1;
    };

    // Load saved state from storage on component mount
    useEffect(() => {
        const loadSavedState = async () => {
            try {
                setLoadingState(true);

                // Load simple data from localStorage
                const savedBizType = localStorage.getItem(STORAGE_KEYS.BIZ_TYPE);
                if (savedBizType) {
                    setBizType(savedBizType);
                }

                const savedParams = localStorage.getItem(STORAGE_KEYS.FILE_PARAMS);
                if (savedParams) {
                    setFileParams(JSON.parse(savedParams));
                }

                const savedWeightedAverage = localStorage.getItem(STORAGE_KEYS.WEIGHTED_AVERAGE);
                if (savedWeightedAverage) {
                    setUseWeightedAverage(JSON.parse(savedWeightedAverage));
                }

                const savedWeights = localStorage.getItem(STORAGE_KEYS.WEIGHTS);
                if (savedWeights) {
                    setWeights(JSON.parse(savedWeights));
                }

                // Load files from IndexedDB
                const savedFiles = await dbHelpers.getFiles();
                if (savedFiles && savedFiles.length > 0) {
                    setFiles(savedFiles);
                }
            } catch (error) {
                console.error('Error loading saved state:', error);
                clearSavedState();
            } finally {
                setLoadingState(false);
            }
        };

        loadSavedState();
    }, []);

    useEffect(() => {
        // Create an event listener for resetting the file upload state
        const handleResetEvent = () => {
            setFiles([]);
            setFileParams([]);
            setBizType('auto');
            setUseWeightedAverage(false);
            setWeights({});
            clearSavedState();

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        // Register the event listener
        window.addEventListener('reset-financial-tool', handleResetEvent);

        // Clean up on component unmount
        return () => {
            window.removeEventListener('reset-financial-tool', handleResetEvent);
        };
    }, []);

    // Save state to storage whenever it changes
    useEffect(() => {
        const saveState = async () => {
            // Save business type
            localStorage.setItem(STORAGE_KEYS.BIZ_TYPE, bizType);

            // Save weighted average setting
            localStorage.setItem(STORAGE_KEYS.WEIGHTED_AVERAGE, JSON.stringify(useWeightedAverage));

            // Save weights
            localStorage.setItem(STORAGE_KEYS.WEIGHTS, JSON.stringify(weights));

            // Save file parameters
            if (fileParams.length > 0) {
                localStorage.setItem(STORAGE_KEYS.FILE_PARAMS, JSON.stringify(fileParams));
            } else {
                localStorage.removeItem(STORAGE_KEYS.FILE_PARAMS);
            }

            // Save files to IndexedDB
            if (files.length > 0) {
                await dbHelpers.saveFiles(files);
            }
        };

        // Only save if not in loading state
        if (!loadingState) {
            saveState();
        }
    }, [files, fileParams, bizType, useWeightedAverage, weights, loadingState]);

    // Initialize weights when files change
    useEffect(() => {
        if (!loadingState) {
            const fullYearFiles = getFullYearFiles();
            const newWeights = { ...weights };
            
            // Initialize weights for full year files if they don't exist
            fullYearFiles.forEach(fileData => {
                const key = `${fileData.year}_${fileData.index}`;
                if (!newWeights[key]) {
                    newWeights[key] = 0.5; // Default weight
                }
            });

            // Remove weights for files that no longer exist
            Object.keys(newWeights).forEach(key => {
                const [year, index] = key.split('_');
                const fileIndex = parseInt(index);
                
                if (!fileParams[fileIndex] || fileParams[fileIndex].year !== year || fileParams[fileIndex].months !== '12') {
                    delete newWeights[key];
                }
            });

            setWeights(newWeights);
        }
    }, [fileParams, loadingState]);

    // Reset weighted average if conditions are not met
    useEffect(() => {
        if (!shouldShowWeightedAverage() && useWeightedAverage) {
            setUseWeightedAverage(false);
        }
    }, [fileParams, useWeightedAverage]);

    // Clear all saved state
    const clearSavedState = async () => {
        localStorage.removeItem(STORAGE_KEYS.FILE_PARAMS);
        localStorage.removeItem(STORAGE_KEYS.BIZ_TYPE);
        localStorage.removeItem(STORAGE_KEYS.WEIGHTED_AVERAGE);
        localStorage.removeItem(STORAGE_KEYS.WEIGHTS);
        await dbHelpers.clearFiles();
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            const updatedFiles = [...files, ...newFiles].slice(0, 6);
            setFiles(updatedFiles);

            // Initialize params for new files
            const newParams = newFiles.map(() => ({
                year: new Date().getFullYear().toString(),
                months: '12',
                source: 'PL'
            }));

            setFileParams(prevParams => {
                const updatedParams = [...prevParams, ...newParams].slice(0, updatedFiles.length);

                if (updatedParams.length < updatedFiles.length) {
                    const missingCount = updatedFiles.length - updatedParams.length;
                    for (let i = 0; i < missingCount; i++) {
                        updatedParams.push({
                            year: new Date().getFullYear().toString(),
                            months: '12',
                            source: 'PL'
                        });
                    }
                }

                return updatedParams;
            });
        }
    };

    // Handle drag events
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    // Handle drop event
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files)
                .filter(file => file.type === 'text/csv' || file.name.endsWith('.csv') ||
                    file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ||
                    file.name.endsWith('.xlsm'));

            if (newFiles.length > 0) {
                const updatedFiles = [...files, ...newFiles].slice(0, 6);
                setFiles(updatedFiles);

                const newParams = newFiles.map(() => ({
                    year: new Date().getFullYear().toString(),
                    months: '12',
                    source: 'PL'
                }));

                setFileParams(prevParams => [...prevParams, ...newParams].slice(0, 6));
            }
        }
    };

    // Remove a file
    const handleRemoveFile = (index) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
        setFileParams(prevParams => prevParams.filter((_, i) => i !== index));

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Update file parameters
    const handleParamChange = (index, param, value) => {
        setFileParams(prevParams =>
            prevParams.map((params, i) =>
                i === index ? { ...params, [param]: value } : params
            )
        );
    };

    // Handle weight change
    const handleWeightChange = (key, value) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
            setWeights(prev => ({
                ...prev,
                [key]: numValue
            }));
        }
    };

    // Reset all state and clear storage
    const handleReset = async () => {
        setFiles([]);
        setFileParams([]);
        setBizType('auto');
        setUseWeightedAverage(false);
        setWeights({});
        await clearSavedState();

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle form submission
    const handleSubmit = (downloadExcel = false) => {
        if (files.length === 0) {
            alert('Please upload at least one CSV file');
            return;
        }

        // Prepare submission data
        const submissionData = {
            useWeightedAverage,
            weights: useWeightedAverage ? weights : null
        };

        onSubmit(files, fileParams, bizType !== 'auto' ? bizType : undefined, downloadExcel, submissionData);
    };

    // Show loading indicator while fetching initial state
    if (loadingState) {
        return (
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography>Loading saved files...</Typography>
                </CardContent>
            </Card>
        );
    }

    const fullYearFiles = getFullYearFiles();
    const partialYearFiles = getPartialYearFiles();
    const showWeightedOption = shouldShowWeightedAverage();

    return (
        <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Upload Financial Data
                </Typography>

                {/* Drag and drop area */}
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: dragActive ? 'primary.main' : 'grey.300',
                        borderRadius: 2,
                        p: 3,
                        mb: 3,
                        textAlign: 'center',
                        backgroundColor: dragActive ? 'rgba(46, 125, 50, 0.04)' : 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: 'primary.light'
                        }
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept=".csv,.xlsx,.xls,.xlsm"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Drag & Drop CSV/Excel Files Here
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        or click to browse (maximum 6 files)
                    </Typography>
                    <Button
                        variant="outlined"
                        component="span"
                        startIcon={<ArticleIcon />}
                    >
                        Upload Files From PC
                    </Button>
                </Box>

                {/* Selected files */}
                {files.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Selected Files
                            </Typography>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleReset}
                                startIcon={<DeleteIcon />}
                            >
                                Clear All
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {files.map((file, index) => (
                            <Paper
                                key={index}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 1,
                                    borderColor: 'grey.300',
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {file.name}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Year</InputLabel>
                                            <Select
                                                value={fileParams[index]?.year || ''}
                                                onChange={(e) => handleParamChange(index, 'year', e.target.value)}
                                                label="Year"
                                            >
                                                {YEARS.map(year => (
                                                    <MenuItem key={year} value={year.toString()}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Months</InputLabel>
                                            <Select
                                                value={fileParams[index]?.months || '12'}
                                                onChange={(e) => handleParamChange(index, 'months', e.target.value)}
                                                label="Months"
                                            >
                                                {MONTHS.map(month => (
                                                    <MenuItem key={month} value={month.toString()}>
                                                        {month} {month === 1 ? 'month' : 'months'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Source</InputLabel>
                                            <Select
                                                value={fileParams[index]?.source || 'PL'}
                                                onChange={(e) => handleParamChange(index, 'source', e.target.value)}
                                                label="Source"
                                            >
                                                <MenuItem value="PL">P&L Statement</MenuItem>
                                                <MenuItem value="TR">Tax Return</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                                        <Tooltip title="Remove file">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveFile(index)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Weighted Average Option */}
                {showWeightedOption && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                            Proforma Calculation Method
                            <Tooltip title="Weighted average allows you to assign different weights to full-year data when calculating proforma for partial years.">
                                <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'text.secondary' }} />
                            </Tooltip>
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useWeightedAverage}
                                    onChange={(e) => setUseWeightedAverage(e.target.checked)}
                                />
                            }
                            label="Use Weighted Average for Proforma Calculation"
                        />

                        {useWeightedAverage && (
                            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Assign weights to full-year files:
                                </Typography>

                                <Grid container spacing={2}>
                                    {fullYearFiles.map((fileData) => {
                                        const key = `${fileData.year}_${fileData.index}`;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={key}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={`${fileData.year} (${fileData.file.name})`}
                                                    value={weights[key] || 0}
                                                    onChange={(e) => handleWeightChange(key, e.target.value)}
                                                    inputProps={{ 
                                                        min: 0, 
                                                        max: 1, 
                                                        step: 0.1 
                                                    }}
                                                    helperText="Weight (0.0 - 1.0)"
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {partialYearFiles.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Partial year files (will use weighted average for proforma):
                                        </Typography>
                                        {partialYearFiles.map((fileData) => (
                                            <Typography variant="body2" key={fileData.index} sx={{ color: 'text.secondary' }}>
                                                • {fileData.year} ({fileData.months} months) - {fileData.file.name}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Business Type Selection */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                        Business Type
                        <Tooltip title="Business type affects financial categorization. Auto-detection is based on presence of COGS.">
                            <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'text.secondary' }} />
                        </Tooltip>
                    </Typography>
                    <RadioGroup
                        row
                        value={bizType}
                        onChange={(e) => setBizType(e.target.value)}
                    >
                        <FormControlLabel value="auto" control={<Radio />} label="Auto Detect" />
                        <FormControlLabel value="Retail" control={<Radio />} label="Retail" />
                        <FormControlLabel value="Service" control={<Radio />} label="Service" />
                    </RadioGroup>
                </Box>

                {/* Error display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Notice about saved data */}
                {files.length > 0 && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Your file selection and settings are saved. You can return to this tab anytime without losing your progress.
                    </Alert>
                )}

                {/* Actions */}
                <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={files.length === 0 || loading}
                            onClick={() => handleSubmit(true)}
                            sx={{ minWidth: 180 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Generate Excel'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default FileUpload;